import React, { useEffect } from "react";
import styled from "styled-components";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  FaRocket,
  FaChartLine,
  FaUsers,
  FaAward,
  FaPlay,
  FaArrowRight,
} from "react-icons/fa";
import CountUp from "react-countup";

const HeroSection = styled.section`
  min-height: 100vh;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%);
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  padding-top: 80px;
`;

const AnimatedBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
      circle at 20% 80%,
      rgba(102, 126, 234, 0.1) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 20%,
      rgba(118, 75, 162, 0.1) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 40% 40%,
      rgba(102, 126, 234, 0.05) 0%,
      transparent 50%
    );
  animation: backgroundShift 20s ease-in-out infinite;

  @keyframes backgroundShift {
    0%,
    100% {
      transform: translateX(0) translateY(0);
    }
    25% {
      transform: translateX(-20px) translateY(-10px);
    }
    50% {
      transform: translateX(20px) translateY(10px);
    }
    75% {
      transform: translateX(-10px) translateY(20px);
    }
  }
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
`;

const FloatingShape = styled(motion.div)`
  position: absolute;
  background: linear-gradient(
    135deg,
    rgba(102, 126, 234, 0.1),
    rgba(118, 75, 162, 0.1)
  );
  border-radius: 50%;
  backdrop-filter: blur(10px);
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 30px;
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 80px;
  align-items: center;
  position: relative;
  z-index: 2;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 60px;
    text-align: center;
  }

  @media (max-width: 768px) {
    padding: 0 20px;
    gap: 40px;
  }
`;

const Content = styled.div`
  color: white;
`;

const Badge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.3);
  padding: 8px 16px;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 30px;
  backdrop-filter: blur(10px);
`;

const Title = styled(motion.h1)`
  font-size: 4.5rem;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 30px;
  background: linear-gradient(135deg, #ffffff 0%, #667eea 50%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 1024px) {
    font-size: 3.5rem;
  }

  @media (max-width: 768px) {
    font-size: 2.8rem;
  }

  @media (max-width: 480px) {
    font-size: 2.2rem;
  }
`;

const Highlight = styled.span`
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled(motion.p)`
  font-size: 1.4rem;
  line-height: 1.6;
  margin-bottom: 40px;
  opacity: 0.9;
  color: #b8b8b8;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const ButtonGroup = styled(motion.div)`
  display: flex;
  gap: 20px;
  margin-bottom: 60px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
  }
`;

const PrimaryButton = styled(motion.button)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 18px 36px;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }
`;

const SecondaryButton = styled(motion.button)`
  background: transparent;
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.3);
  padding: 16px 34px;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

const StatsContainer = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const StatItem = styled(motion.div)`
  text-align: center;
  padding: 25px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  }
`;

const StatIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 15px;
  color: #667eea;
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 8px;
  background: linear-gradient(135deg, #ffffff 0%, #667eea 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const StatLabel = styled.div`
  font-size: 1rem;
  opacity: 0.8;
  color: #b8b8b8;
`;

const VisualContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 600px;

  @media (max-width: 1024px) {
    height: 400px;
  }
`;

const MainCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 30px;
  padding: 40px;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  max-width: 400px;
  width: 100%;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
`;

const CardHeader = styled.div`
  text-align: center;
  margin-bottom: 30px;
`;

const CardTitle = styled.h3`
  color: #ffffff;
  font-size: 1.5rem;
  margin-bottom: 10px;
  font-weight: 700;
`;

const CardSubtitle = styled.p`
  color: #b8b8b8;
  font-size: 0.9rem;
`;

const CardContent = styled.div`
  color: #ffffff;
  font-size: 0.95rem;
  line-height: 1.6;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 15px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &:last-child {
    border-bottom: none;
  }
`;

const FeatureIcon = styled.div`
  width: 20px;
  height: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: white;
`;

const FloatingCards = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
`;

const FloatingCard = styled(motion.div)`
  position: absolute;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 20px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-size: 0.8rem;
  color: #ffffff;
  max-width: 150px;
`;

const Hero = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const controls = useAnimation();

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <HeroSection id="hero">
      <AnimatedBackground />

      <FloatingElements>
        <FloatingShape
          style={{ width: "100px", height: "100px", top: "20%", left: "10%" }}
          animate={{
            y: [0, -20, 0],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <FloatingShape
          style={{ width: "60px", height: "60px", top: "60%", right: "15%" }}
          animate={{
            y: [0, 20, 0],
            rotate: [360, 180, 0],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <FloatingShape
          style={{ width: "80px", height: "80px", bottom: "20%", left: "20%" }}
          animate={{
            y: [0, -15, 0],
            x: [0, 10, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </FloatingElements>

      <Container ref={ref}>
        <Content>
          <Badge
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            <FaRocket />
            专业广告投放服务
          </Badge>

          <Title
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
            transition={{ duration: 0.8 }}
          >
            让您的品牌在
            <br />
            数字世界中
            <br />
            <Highlight>闪耀光芒</Highlight>
          </Title>

          <Subtitle
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            深圳二三三数字科技有限公司成立于2014年，专注于为企业提供
            <br />
            全方位的数字营销解决方案，助力品牌实现快速增长
          </Subtitle>

          <ButtonGroup
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <PrimaryButton
              onClick={() => scrollToSection("contact")}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              立即咨询
              <FaArrowRight />
            </PrimaryButton>
            <SecondaryButton
              onClick={() => scrollToSection("portfolio")}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaPlay />
              查看案例
            </SecondaryButton>
          </ButtonGroup>

          <StatsContainer
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <StatItem
              whileHover={{ scale: 1.05, y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <StatIcon>
                <FaRocket />
              </StatIcon>
              <StatNumber>
                <CountUp end={500} duration={2} />+
              </StatNumber>
              <StatLabel>成功案例</StatLabel>
            </StatItem>
            <StatItem
              whileHover={{ scale: 1.05, y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <StatIcon>
                <FaChartLine />
              </StatIcon>
              <StatNumber>
                <CountUp end={300} duration={2} />%
              </StatNumber>
              <StatLabel>平均ROI提升</StatLabel>
            </StatItem>
            <StatItem
              whileHover={{ scale: 1.05, y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <StatIcon>
                <FaUsers />
              </StatIcon>
              <StatNumber>
                <CountUp end={1000} duration={2} />+
              </StatNumber>
              <StatLabel>合作客户</StatLabel>
            </StatItem>
            <StatItem
              whileHover={{ scale: 1.05, y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <StatIcon>
                <FaAward />
              </StatIcon>
              <StatNumber>
                <CountUp end={10} duration={2} />年
              </StatNumber>
              <StatLabel>行业经验</StatLabel>
            </StatItem>
          </StatsContainer>
        </Content>

        <VisualContainer>
          <MainCard
            initial={{ opacity: 0, scale: 0.8, rotateY: -15 }}
            animate={
              inView
                ? { opacity: 1, scale: 1, rotateY: 0 }
                : { opacity: 0, scale: 0.8, rotateY: -15 }
            }
            transition={{ duration: 1, delay: 0.8 }}
            whileHover={{ scale: 1.02, rotateY: 5 }}
          >
            <CardHeader>
              <CardTitle>专业服务</CardTitle>
              <CardSubtitle>全方位数字营销解决方案</CardSubtitle>
            </CardHeader>

            <CardContent>
              <FeatureList>
                <FeatureItem>
                  <FeatureIcon>✓</FeatureIcon>
                  <span>精准投放策略制定</span>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>✓</FeatureIcon>
                  <span>多渠道广告优化</span>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>✓</FeatureIcon>
                  <span>实时数据监控分析</span>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>✓</FeatureIcon>
                  <span>24/7专业客服支持</span>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>✓</FeatureIcon>
                  <span>品牌创意设计</span>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>✓</FeatureIcon>
                  <span>效果优化服务</span>
                </FeatureItem>
              </FeatureList>
            </CardContent>
          </MainCard>

          <FloatingCards>
            <FloatingCard
              style={{ top: "10%", right: "-20%" }}
              animate={{
                y: [0, -10, 0],
                rotate: [0, 5, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
                ROI提升
              </div>
              <div>平均300%+</div>
            </FloatingCard>

            <FloatingCard
              style={{ bottom: "20%", left: "-25%" }}
              animate={{
                y: [0, 10, 0],
                rotate: [0, -5, 0],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
                客户满意度
              </div>
              <div>98%+</div>
            </FloatingCard>
          </FloatingCards>
        </VisualContainer>
      </Container>
    </HeroSection>
  );
};

export default Hero;
